/**
 * Goal History Page
 * List past goals with status; tap to view completion summary (story cards)
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { goalsService } from '@/services/goals.service';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Target, ChevronRight, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PAGES_PER_KHATM } from '@/types/goals';
import type { Goal } from '@/types/goals';

function formatDate(s: string): string {
  try {
    const d = new Date(s + 'T00:00:00');
    return d.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return s;
  }
}

function statusLabel(status: Goal['status']): string {
  switch (status) {
    case 'active':
      return 'กำลังทำ';
    case 'completed':
      return 'เสร็จสิ้น';
    case 'replaced':
      return 'ถูกแทนที่';
    default:
      return status;
  }
}

export function GoalHistoryPage() {
  const navigate = useNavigate();

  const { data: goals = [], isLoading } = useQuery({
    queryKey: ['goals'],
    queryFn: () => goalsService.listGoals(),
  });

  const handleViewSummary = (goalId: string) => {
    navigate(`/goal-completed/${goalId}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur px-4 py-4">
        <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
          <Target className="w-6 h-6 text-primary" />
          ประวัติเป้าหมาย
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          ดูสรุปและสถิติของเป้าหมายที่ผ่านมา
        </p>
      </header>

      <main className="p-4 pb-24 space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-28 w-full rounded-xl" />
            ))}
          </div>
        ) : goals.length === 0 ? (
          <Card className="border-2 border-dashed border-border">
            <CardContent className="p-8 text-center">
              <div className="inline-flex justify-center w-16 h-16 rounded-full bg-muted mb-3">
                <BookOpen className="w-8 h-8 text-muted-foreground m-auto" />
              </div>
              <p className="font-medium text-foreground mb-1">ยังไม่มีเป้าหมาย</p>
              <p className="text-sm text-muted-foreground mb-4">
                ตั้งเป้าหมายแรกที่แดชบอร์ด
              </p>
              <Button onClick={() => navigate('/dashboard')}>ไปแดชบอร์ด</Button>
            </CardContent>
          </Card>
        ) : (
          <ul className="space-y-3">
            {goals.map((goal) => {
              const totalPages = PAGES_PER_KHATM * goal.khatm_count;
              const canViewSummary = goal.status === 'completed' || goal.status === 'replaced';
              return (
                <li key={goal.id}>
                  <Card
                    className={cn(
                      'transition-all hover:shadow-md',
                      canViewSummary && 'cursor-pointer hover:border-primary/30'
                    )}
                    onClick={() => canViewSummary && handleViewSummary(goal.id)}
                  >
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <span
                        className={cn(
                          'rounded-full px-2.5 py-0.5 text-xs font-medium',
                          goal.status === 'active' && 'bg-primary/20 text-primary',
                          goal.status === 'completed' &&
                            'bg-green-500/20 text-green-700 dark:text-green-400',
                          goal.status === 'replaced' && 'bg-muted text-muted-foreground'
                        )}
                      >
                        {statusLabel(goal.status)}
                      </span>
                      {canViewSummary && (
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      )}
                    </CardHeader>
                    <CardContent className="space-y-1">
                      <p className="font-semibold text-foreground">
                        {goal.khatm_count.toLocaleString('th-TH')} Khatm
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(goal.start_date)} – {formatDate(goal.end_date)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        เป้า {totalPages.toLocaleString('th-TH')} หน้า
                      </p>
                    </CardContent>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}

export default GoalHistoryPage;
